<template lang="pug">
	.audio-tracker-insertion--voice-details(v-if="voiceData")
		.flex-col.space-medium
			.flex-row
				slot(name="top")
			.flex-row.justify
				.left.flex-row
					.font-big.upper {{voice.text}}
				.right.flex-row
					slot(name="top-right")
			Loader(v-if="isLoading", :text="$tCaption('Loading', '@actions') + '..'")
			.flex-row.space-extralarge(v-else-if="voiceDetails")
				.flex-col.space-medium.flex-1
					.flex-row.center.align-center
						.voice-image(v-if="voice.voice.ImageUrl", :style="{ '--image': `url('${voice.voice.ImageUrl}')` }")
					.flex-col
						.font-small.grey.upper {{$tCaption('Provider')}}
						span {{providerTypes[voice.voice.TtsProviderType] && providerTypes[voice.voice.TtsProviderType].name}}
					.flex-col(v-if="!compact && voice.voice.Name !== voice.displayName")
						.font-small.grey.upper {{$tCaption('Provider voice name')}}
						span {{voice.voice.Name}}
					.flex-col
						.font-small.grey.upper {{$tCaption('Language (Dialect)')}}
						span {{voice.mainLanguage.text}}
					.flex-col.space
						.font-small.grey.upper {{$tCaption('Voice demo')}}
						.flex-col.space
							.voice-demo.lr-padding.flex-row.space.align-center(v-for="demo in (voiceDetails.VoiceDemos || [])", )
								.play.clickable(v-show="isPlayingId !== demo.Oid", @click="listenPreview(demo)")
									fas(:icon="isInitializingId === demo.Oid ? 'spinner' : 'play'", :spin="isInitializingId === demo.Oid")
								.stop.clickable(v-show="isPlayingId === demo.Oid", @click="stopListenPreview")
									i.fas.fa-stop
								span {{demo.Name}}
					.flex-col
						.font-small.grey.upper {{$tCaption('Gender')}}
						span {{voice.voice.Gender}}
				.flex-col.flex-2.space-medium
					.flex-col(v-if="voiceDetails.About")
						.font-small.grey.upper {{$tCaption('About')}}
						DxScrollView(:height="100")
							.about-area {{voiceDetails.About}}

					.flex-col.space(v-if="voiceDetails.IabCategories && voiceDetails.IabCategories.length && creativeIabCategories")
						.flex-col
							.font-small.grey.upper {{ $tCaption('Restricted Iab Categories') }}
							.flex-col.space
								div {{ voiceIabCategoriesNamesStr }}
								.error-colored(
									v-if="matchedIabCategories.length > 0"
									) {{ $tCaption('Matched') }}: {{ matchedIabCategoriesNamesStr }}

								.flex-col(v-if="voiceIabCategories.length > 0")
									template(v-if="creativeIabCategories.length > 0 && matchedIabCategories.length > 0")
										.error-colored {{ $tCaption('The voice you have selected has restrictions for certain advertiser categories.') }}
										.error-colored {{ $tCaption('The voice may not be used for the categories you have selected.') }}
									template(v-else-if="creativeIabCategories.length === 0")
										.error-colored {{ $tCaption('The voice you have selected has restrictions for certain advertiser categories.') }}
										.error-colored {{ $tCaption('Please select the applicable IAB Categories for the Creative.') }}

					.flex-row.space-extralarge
						.flex-col.space-medium.flex-1
							.flex-col
								.font-small.grey.upper {{$tCaption('Ages')}}
								span {{(voiceDetails.Ages || []).map(f => f.Name).join(', ') || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Accents')}}
								span {{(voiceDetails.Accents || []).map(f => f.Name).join(', ') || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Skills')}}
								span {{(voiceDetails.Skills || []).map(f => f.Name).join(', ') || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Styles list')}}
								span {{(voiceDetails.VoiceStyles || []).map(f => f && f.Name).join(', ') || '-'}}
						.flex-col.space-medium.flex-2(v-if="voice.isHuman")
							.flex-col
								.font-small.grey.upper {{$tCaption('Computer and software')}}
								span {{voiceDetails.ComputerAndSoftware || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Live directed sessions')}}
								span {{voiceDetails.LiveDirectedSessions || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Micropohone')}}
								span {{voiceDetails.Micropohone || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Special equimpment')}}
								span {{voiceDetails.SpecialEquimpment || '-'}}
							.flex-col
								.font-small.grey.upper {{$tCaption('Turnaround time')}}
								span {{$moment(voiceDetails.TurnaroundTime).toDate() > new Date(1901,1,1) ? $moment(voiceDetails.TurnaroundTime).format('LLL') : '-'}}

					.flex-col(v-if="!hideNLCSSounds && voiceDetails.NlcSounds && voiceDetails.NlcSounds.length")
						.font-small.grey.upper {{$tCaption('Nlc Sounds')}}
						span {{(voiceDetails.NlcSounds || []).filter(f => f.NlcsType).map(f => f.NlcsType.Description).join(', ') || '-'}}
</template>

<script>
	import Loader from 'app-core/Loader.vue';
	import { SPEECH_PROVIDER_TYPES } from 'app-core/constants';
	import DxScrollView from 'devextreme-vue/scroll-view';

	export default {
		translationCategory: 'insertion',
		name: 'audio-tracker-insertion--voice-details',
		components: { Loader, DxScrollView },
		props: ['voice', 'creativeIabCategories', 'hideNLCSSounds', 'compact'],
		data() {
			return {
				voiceDetails: null,
				isLoading: 0,
				playingAudio: null,
				isPlayingId: null,
				isInitializingId: null,
				providerTypes: SPEECH_PROVIDER_TYPES.toLocale('name', this)
			}
		},
		computed: {
			voiceData() {
				return this.voice.voice;
			},
			voiceIabCategories() {
				return this.voiceDetails?.IabCategories
					?.reduce((acc, cat) => {
						return [
							...acc,
							{
								Code: cat.Code,
								Name: cat.Name,
								Oid: cat.Oid,
							},
							...(cat.SubCategories || []),
						]
					}, []) || [];
			},
			matchedIabCategories() {
				let creativeIab = [...(this.creativeIabCategories || [])].map(c => c.Oid);
				let voiceIab = [...this.voiceIabCategories].map(c => c.Oid);

				return voiceIab.filter(c => creativeIab.includes(c)) || [];
			},
			matchedIabCategoriesNamesStr(){
				return [...this.voiceIabCategories]
					.filter(c => this.matchedIabCategories.includes(c.Oid))
					.map((sub) => sub.Name)
					.join(', ');
			},
			voiceIabCategoriesNamesStr() {
				return [...this.voiceIabCategories]
					.filter(c => !this.matchedIabCategories.includes(c.Oid))
					.map((sub) => sub.Name)
					.join(', ');
			},
		},
		mounted() {
			if (__DEBUG__)
				window.voiceDetails = this;
			this.loadVoiceDetails();
		},
		destroyed() {
			this.stopListenPreview();
		},
		watch: {
			voice(v) {
				if (v)
					this.loadVoiceDetails();
			},
			voiceIabCategories(v) {
				this.$emit('getRestrictedIabCategories', v);
			},
			matchedIabCategories(v) {
				this.$emit('getMatchedIabCategories', v);
			},
			isLoading(v) {
				this.$emit('isLoadingVoiceDetails', Boolean(v));
			},
		},
		methods: {
			processVoiceData(data) {
				return {
					IabCategories: [],
					...this.voice.voice,
					...data
				}
			},
			async loadVoiceDetails() {
				if (!this.voice) return;
				if (this.voiceData?.Oid && this.voiceData.Oid !== '00000000-0000-0000-0000-000000000000') {
					let voiceDetails = this.voice.__voiceDetails;
					if (!voiceDetails) {
						this.isLoading++;
						voiceDetails = this.voice.__voiceDetails = await this.$Data.getVoiceDetails(this.voiceData?.Oid).finally(() => this.isLoading--);
					}
					this.voiceDetails = voiceDetails || this.processVoiceData(this.voiceData);
					this.voice.__voiceDetails = voiceDetails;
				} else {
					this.voiceDetails = this.processVoiceData(this.voiceData);
				}
				this.$emit('voice-details', this.voiceDetails);
			},
			async listenPreview(item) {
				if (__DEBUG__)
					console.debug('Listen preview', item);
				const preview = item.AudioFile;
				this.stopListenPreview();
				this.playingAudio = new Audio(preview.AzureFileUrl);
				this.playingAudio.play();
				this.isInitializingId = item.Oid;
				this.playingAudio.addEventListener('canplay', () => {
					this.isPlayingId = item.Oid;
					this.isInitializingId = null;
				})
				const end = () => {
					if (this.isPlayingId === item.Oid) {
						this.isPlayingId = null;
					}
				};
				this.playingAudio.addEventListener('error', end);
				this.playingAudio.addEventListener('ended', end);
				this.playingAudio.addEventListener('pause', end);
			},
			stopListenPreview() {
				this.isPlayingId = null;
				if (this.playingAudio)
					this.playingAudio.pause();
			},
		}
	};
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";

	.audio-tracker-insertion--voice-details {
		.grey {
			color: #b9b9b9;
			font-weight: 100;
		}

		.voice-image {
			background: var(--image);
			min-width: 200px;
			min-height: 150px;
			max-width: 200px;
			max-height: 150px;
			width: 100%;
			background-size: cover;
			background-position: center;
			border-radius: 80px;
			border: 12px solid rgb(244 244 244);
		}
	}
</style>
